import React, { useContext } from "react";
import { ActionType, AppState, UserLanguage } from "../../@types";
import { Alert } from "@cmsgov/ds-medicare-gov";
import { getLoginUrl } from "../../helpers/loginHelpers";
import { FocusableHeader, LoginLogoutButton } from "../../components";
import { FormattedHTMLMessage } from "react-intl";
import { Link } from "react-router-dom";
import { LDFlagSet } from "launchdarkly-js-sdk-common";
import {
  sendTealiumEvent,
  initiateLogin,
  useTranslate,
  isOutsideOpenEnrollment,
} from "../../helpers";
import classnames from "classnames";
import { getRoutes } from "./LandingPageHelpers";
import { FormattedMessage } from "react-intl-phraseapp";
import { AppContext } from "../../app/store";

export const SubHeader: React.FC<{ flags: LDFlagSet }> = ({
  flags: { mctYpOpenEnrollment, mctYpPublicPreview, mctCurrentPlanYear },
}) => {
  const returnValue = [];
  const t = useTranslate();

  !mctYpPublicPreview &&
    returnValue.push(
      <FormattedMessage
        id="ooe_landing.shop"
        values={{
          drugPlans: (
            <span className="ds-u-font-weight--bold">
              <FormattedHTMLMessage id="ooe_landing.pdp_plans" />
            </span>
          ),
          medicareAdvantagePlans: (
            <span className="ds-u-font-weight--bold">
              {t("ooe_landing.mapd_plans")}
            </span>
          ),
        }}
      />
    );

  mctYpPublicPreview &&
    returnValue.push(
      <FormattedMessage
        id="public_preview_landing.preview_plans"
        values={{
          year: mctCurrentPlanYear + 1,
          drugPlans: (
            <span className="ds-u-font-weight--bold">
              <FormattedHTMLMessage id="ooe_landing.pdp_plans" />
            </span>
          ),
          medicareAdvantagePlans: (
            <span className="ds-u-font-weight--bold">
              {t("ooe_landing.mapd_plans")}
            </span>
          ),
        }}
      />
    );

  mctYpOpenEnrollment &&
    returnValue.push(
      <div
        id="openEnrollmentText"
        className="ds-u-margin-top--1 ds-u-md-margin-top--0"
      >
        <FormattedMessage
          id="oe_landing.login_prompt"
          values={{ year: mctCurrentPlanYear + 1 }}
        />
      </div>
    );

  mctYpPublicPreview &&
    returnValue.push(
      <div>
        <FormattedHTMLMessage
          id="public_preview.landing.enroll_oct_15"
          values={{ year: mctCurrentPlanYear + 1 }}
        />
      </div>
    );

  return <h2 className="LandingPage__heroSubheader">{returnValue}</h2>;
};

export interface HeroInfoProps {
  state: AppState;
  flags: LDFlagSet;
}

const getPageTitle = ({
  mctYpOpenEnrollment,
  mctYpPublicPreview,
}: LDFlagSet): string => {
  let pageTitle = "";

  if (mctYpOpenEnrollment) {
    pageTitle = "oe_landing.find_plan";
  } else if (mctYpPublicPreview) {
    pageTitle = "public_preview.landing.header";
  } else {
    pageTitle = "ooe_landing.find_plan";
  }

  return pageTitle;
};

export const HeroInfo: React.FC<HeroInfoProps> = ({ state, flags }) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);

  const {
    questionsRouteForNextYear,
    questionsRouteForThisYear,
    beneRouteForNextYear,
    beneRouteForThisYear,
  } = getRoutes(flags.mctCurrentPlanYear);

  const outsideOpenEnrollment = isOutsideOpenEnrollment(flags);

  return (
    <div className="LandingPage__heroInfo">
      {state.mbpHandoffFailed && (
        <Alert
          role="alert"
          className="ds-l-lg-col--8 ds-u-text-align--left ds-u-margin-bottom--0"
          variation="error"
          hideIcon={true}
          heading={t("landing_page.mbp_error.heading")}
        >
          <a href={getLoginUrl(state.language === UserLanguage.SPANISH)}>
            {t("landing_page.mbp_error.link_text")}
          </a>{" "}
          {t("landing_page.mbp_error.start_plan_search")}
        </Alert>
      )}

      <FocusableHeader
        className={classnames("LandingPage__heroHeader", {
          "ds-u-margin-top--2": state.mbpHandoffFailed,
        })}
        text={{
          id: getPageTitle(flags),
          values: { year: flags.mctCurrentPlanYear + 1 },
        }}
        ignoreFocus={state.mbpHandoffFailed}
      />

      <SubHeader flags={flags} />

      <div>
        {state.beneficiary ? (
          <Link
            to={
              !flags.mctYpOutsideOpenEnrollment
                ? beneRouteForNextYear
                : beneRouteForThisYear
            }
            className="LandingPage__button ds-c-button ds-u-margin-bottom--2"
          >
            <FormattedMessage
              id="landing_page.shop_for_next_year_plans"
              values={{
                year: !flags.mctYpOutsideOpenEnrollment
                  ? flags.mctCurrentPlanYear + 1
                  : flags.mctCurrentPlanYear,
              }}
            />
          </Link>
        ) : (
          <LoginLogoutButton
            className="LandingPage__button ds-u-margin-top--1 ds-u-md-margin-top--4"
            size="big"
            id="e2e-landing-login"
            loginKey="ooe_landing.create_or_login"
            hideIfDisabled={true}
            onLogin={(): void => {
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans Landing Page - Login", "Login");
              dispatch({
                type: ActionType.UPDATE_YEAR,
                payload: (!flags.mctYpOutsideOpenEnrollment
                  ? flags.mctCurrentPlanYear + 1
                  : flags.mctCurrentPlanYear
                ).toString(),
              });
              initiateLogin(state.language === UserLanguage.SPANISH);
            }}
          />
        )}
      </div>

      {!state.beneficiary && (
        <Link
          to={
            !outsideOpenEnrollment || flags.mctYpOeNextYear
              ? questionsRouteForNextYear
              : questionsRouteForThisYear
          }
          onClick={(): void => {
            // istanbul ignore next: tested elsewhere
            sendTealiumEvent(
              "Find Plans Landing Page - Login",
              "Continue without logging in 1"
            );
          }}
          className="mct-link--inverse e2e-anonymous ds-u-font-size--h4"
        >
          {t("ooe_landing.continue_without_login")}
        </Link>
      )}
    </div>
  );
};
